/**
 * Pure HTTP retry policy for the shared Axios client (Requirements 2.1, 2.3-2.6).
 *
 * Nothing here performs I/O, sleeps, or touches a timer: `classifyRetry` returns a
 * decision and a delay, and the response interceptor is the only thing that acts on
 * it. `now` is injectable so the HTTP-date branch of `Retry-After` is testable
 * without freezing the clock.
 */

/** Maximum number of rate-limit retries before the error reaches the caller (Req 2.6). */
export const MAX_RATE_LIMIT_RETRIES = 3;

/** Flat backoff used when the server sends no usable `Retry-After` (Req 2.5). */
export const DEFAULT_RETRY_AFTER_MS = 5000;

/** Upper bound on any single wait, guarding absurd server-supplied values. */
export const MAX_RETRY_DELAY_MS = 60000;

/** Retry actions returned by `classifyRetry`. */
export const RETRY_ACTION = {
  REFRESH_RETRY: "refresh-retry",
  WAIT_RETRY: "wait-retry",
  FAIL: "fail",
};

/** Reasons attached to a decision, used to pick the user-facing message. */
export const RETRY_REASON = {
  UNAUTHORIZED: "unauthorized",
  ACCESS_DENIED: "access-denied",
  RATE_LIMITED: "rate-limited",
  NOT_RETRYABLE: "not-retryable",
};

/** Strict decimal form: optional sign, digits, optional fractional part. */
const DELTA_SECONDS_PATTERN = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/;

/** Milliseconds from a seconds value, never negative (Req 2.4). */
function secondsToMs(seconds) {
  if (!Number.isFinite(seconds)) return null;
  return Math.max(0, Math.round(seconds * 1000));
}

/**
 * Parses a `Retry-After` value into milliseconds.
 *
 * Both forms defined by RFC 9110 are accepted: delta-seconds (a bare number, as a
 * string or an actual number) and an HTTP-date, whose distance from `now` is used.
 * Both are floored at zero, so a negative delta or a date in the past means "retry
 * immediately" rather than a negative wait. Numeric parsing is tried first, so a
 * bare number is never mistaken for a lenient date.
 *
 * @param {string|number|null|undefined} headerValue
 * @param {number} [now] epoch ms used for the HTTP-date branch
 * @returns {number|null} milliseconds, or null when absent or unparseable
 */
export function parseRetryAfter(headerValue, now = Date.now()) {
  if (typeof headerValue === "number") {
    return secondsToMs(headerValue);
  }

  if (typeof headerValue !== "string") return null;

  const trimmed = headerValue.trim();
  if (trimmed === "") return null;

  if (DELTA_SECONDS_PATTERN.test(trimmed)) {
    return secondsToMs(Number(trimmed));
  }

  const target = Date.parse(trimmed);
  if (!Number.isFinite(target)) return null;

  const base = Number.isFinite(now) ? now : Date.now();
  return Math.max(0, Math.round(target - base));
}

/**
 * Resolves the wait before the next attempt.
 *
 * Flat, not exponential: Req 2.5 asks for a literal 5-second default, so an
 * unparseable or absent header yields exactly `DEFAULT_RETRY_AFTER_MS`. The clamp to
 * `[0, MAX_RETRY_DELAY_MS]` exists only to bound hostile or mistaken server values.
 *
 * @param {string|number|null|undefined} headerValue
 * @param {number} [now]
 * @returns {number} a finite, non-negative delay in milliseconds
 */
export function resolveRetryDelayMs(headerValue, now = Date.now()) {
  const parsed = parseRetryAfter(headerValue, now);
  const delay = parsed === null ? DEFAULT_RETRY_AFTER_MS : parsed;
  return Math.min(Math.max(delay, 0), MAX_RETRY_DELAY_MS);
}

/** Retry counts arrive from request config, so treat anything odd as "none yet". */
function readAttempts(attempts) {
  if (typeof attempts !== "number" || !Number.isFinite(attempts)) return 0;
  return Math.max(0, Math.floor(attempts));
}

function decision(action, reason, delayMs = 0) {
  return { action, delayMs, reason };
}

/**
 * Decides what to do with a failed response.
 *
 * - **401** → one `refresh-retry` per original request; a second 401 after the
 *   refresh fails, which is what drives the logout/redirect in Req 2.2.
 * - **403** → immediate `fail` with `access-denied`, never a refresh (Req 2.3).
 *   The attempt count is irrelevant: a permission failure does not become allowed
 *   by asking again.
 * - **429** → `wait-retry` while fewer than `MAX_RATE_LIMIT_RETRIES` retries have
 *   happened, using the `Retry-After` delay or the 5-second default; afterwards
 *   `fail` with `rate-limited` (Req 2.4-2.6).
 * - anything else (including network errors with no status) → `fail`.
 *
 * `retryAfter` is the raw header value; it is needed here to populate `delayMs` for
 * the 429 branch.
 *
 * @param {{ status?: number|null, attempts?: number, hasAuthRetried?: boolean,
 *           retryAfter?: string|number|null, now?: number }} [input]
 * @returns {{ action: 'refresh-retry'|'wait-retry'|'fail', delayMs: number, reason: string }}
 */
export function classifyRetry({ status, attempts, hasAuthRetried, retryAfter, now } = {}) {
  const attemptCount = readAttempts(attempts);

  if (status === 401) {
    return hasAuthRetried
      ? decision(RETRY_ACTION.FAIL, RETRY_REASON.UNAUTHORIZED)
      : decision(RETRY_ACTION.REFRESH_RETRY, RETRY_REASON.UNAUTHORIZED);
  }

  if (status === 403) {
    return decision(RETRY_ACTION.FAIL, RETRY_REASON.ACCESS_DENIED);
  }

  if (status === 429) {
    if (attemptCount < MAX_RATE_LIMIT_RETRIES) {
      return decision(
        RETRY_ACTION.WAIT_RETRY,
        RETRY_REASON.RATE_LIMITED,
        resolveRetryDelayMs(retryAfter, now === undefined ? Date.now() : now)
      );
    }
    return decision(RETRY_ACTION.FAIL, RETRY_REASON.RATE_LIMITED);
  }

  return decision(RETRY_ACTION.FAIL, RETRY_REASON.NOT_RETRYABLE);
}
